import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Api } from 'bimplus-websdk';
import { BIMPLUS_API } from '@web-portal/domains/shared/bimplus-core';
import type { Task } from '@web-portal/domains/shared/domain-models';
import type {
  BcfExportRequest,
  BcfImportResult,
  TaskComment,
  UpdateTaskPayload,
} from './task-adapter.interface';

export type {
  BcfExportRequest,
  BcfImportResult,
  TaskComment,
  UpdateTaskPayload,
} from './task-adapter.interface';

type TaskResponsiblePayload = NonNullable<UpdateTaskPayload['responsibles']>[number];

function toResponsiblesPayload(
  responsibles: readonly Pick<TaskResponsiblePayload, 'id' | 'email'>[] | undefined
): TaskResponsiblePayload[] {
  return responsibles?.map(({ id, email }) => ({ id, email })) ?? [];
}

function toResponsibleEmails(responsibles: TaskResponsiblePayload[]): string {
  return responsibles
    .map(({ email }) => email)
    .filter(Boolean)
    .join(',');
}

function resolveResponsibles(task: Task, patch: UpdateTaskPayload): TaskResponsiblePayload[] {
  if (patch.responsibles !== undefined) {
    return toResponsiblesPayload(patch.responsibles);
  }

  if (task.responsibles.length > 0) {
    return toResponsiblesPayload(task.responsibles);
  }

  if (task.responsible) {
    return toResponsiblesPayload([task.responsible]);
  }

  return [];
}

/**
 * Builds a full API payload by merging the current task with a partial update.
 * The BIM+ PUT endpoint replaces the entire resource, so we must send all fields.
 */
function buildFullPayload(task: Task, patch: UpdateTaskPayload): Record<string, unknown> {
  const responsibles = resolveResponsibles(task, patch);
  const responsibleEmails = patch.responsibleEmails ?? toResponsibleEmails(responsibles);

  return {
    id: task.id,
    name: task.name,
    description: task.description,
    status: task.status,
    priority: task.priority,
    classification: task.classification,
    type: task.type,
    dueDate: task.dueDate ?? null,
    cc: task.cc,
    viewers: task.viewers,
    ...patch,
    responsible: null,
    responsibles,
    responsibleEmails,
  };
}

/**
 * TasksAdapterService - Adapter over bimplus-websdk Issues API.
 *
 * Handles single-task CRUD operations via the `api.issues` namespace.
 * For listing tasks by project, use ProjectsAdapterService.getTasks().
 */
@Injectable({
  providedIn: 'root',
})
export class TasksAdapterService {
  private readonly api: Api = inject(BIMPLUS_API);

  /**
   * Retrieves a single task/issue by ID.
   *
   * @param taskId - The GUID of the task to retrieve.
   * @returns Observable of Task.
   */
  getTask(taskId: string): Observable<Task> {
    return this.api.rxRequest(this.api.issues.get(taskId));
  }

  /**
   * Updates a task/issue.
   * The BIM+ API replaces the entire resource on PUT, so we merge the
   * current task with the partial update to avoid wiping unchanged fields.
   *
   * @param taskId - The GUID of the task to update.
   * @param currentTask - The full current task object.
   * @param data - Partial task data to update.
   * @returns Observable of updated Task.
   */
  updateTask(taskId: string, currentTask: Task, data: UpdateTaskPayload): Observable<Task> {
    const payload = buildFullPayload(currentTask, data);
    return this.api.rxRequest(this.api.issues.put(taskId, JSON.stringify(payload)));
  }

  /**
   * Retrieves all comments for a task/issue.
   *
   * @param taskId - The GUID of the task.
   * @returns Observable of TaskComment array.
   */
  getComments(taskId: string): Observable<TaskComment[]> {
    return this.api.rxRequest(this.api.issues.getComments(taskId));
  }

  /**
   * Adds a comment to a task/issue.
   *
   * @param taskId - The GUID of the task.
   * @param text - The comment text.
   * @returns Observable of the created TaskComment.
   */
  addComment(taskId: string, text: string): Observable<TaskComment> {
    return this.api.rxRequest(this.api.issues.postComment(taskId, JSON.stringify({ text })));
  }

  /**
   * Exports a set of issues/tasks to a BCF file.
   *
   * The API returns an object with an `attachment_id` referencing the generated
   * BCF archive. Use the attachment ID to download the actual file.
   *
   * @param data - Export request payload (issue IDs + suggested file name).
   * @param version - BCF schema version: '20' for BCF 2.1, '30' for BCF 3.0.
   * @returns Observable of the export result containing the attachment ID.
   */
  exportIssuesToBcf(
    data: BcfExportRequest,
    version: string
  ): Observable<{ attachment_id: string }> {
    return this.api.rxRequest(this.api.issues.exportBcf(null, JSON.stringify(data), version));
  }

  /**
   * Imports tasks/issues from a BCF file.
   *
   * @param projectId - The project ID where imported issues should be created.
   * @param data - Multipart form data with key `data` containing the uploaded file.
   */
  importIssuesFromBcf(projectId: string, data: FormData): Observable<BcfImportResult> {
    return this.api.rxRequest(this.api.issues.importBcf(projectId, data));
  }
}
